import { useState, useEffect } from "react";
import {
  TrendingUp,
  Clock,
  Star,
  FileText,
  BarChart3,
  Calendar,
} from "lucide-react";
import medicalDB from "../services/database";

const DraftAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    totalDrafts: 0,
    totalUsage: 0,
    popularDrafts: [],
    recentDrafts: [],
    categoryStats: {},
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Get all drafts for statistics
      const allDrafts = await medicalDB.getAllDrafts();
      const popularDrafts = await medicalDB.getPopularDrafts(5);
      const recentDrafts = await medicalDB.getRecentDrafts(5);

      // Calculate category statistics
      const categoryStats = {};
      allDrafts.forEach((draft) => {
        if (!categoryStats[draft.category]) {
          categoryStats[draft.category] = {
            count: 0,
            totalUsage: 0,
          };
        }
        categoryStats[draft.category].count++;
        categoryStats[draft.category].totalUsage += draft.usageCount || 0;
      });

      const totalUsage = allDrafts.reduce(
        (sum, draft) => sum + (draft.usageCount || 0),
        0
      );

      setAnalytics({
        totalDrafts: allDrafts.length,
        totalUsage,
        popularDrafts,
        recentDrafts,
        categoryStats,
      });
    } catch (error) {
      console.error("Error loading draft analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-600">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <BarChart3 className="h-6 w-6 text-medical-blue" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Draft Analytics
          </h3>
          <p className="text-sm text-gray-600">Usage statistics and trends</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">
                Total Templates
              </p>
              <p className="text-2xl font-bold text-blue-900">
                {analytics.totalDrafts}
              </p>
            </div>
            <FileText className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Total Usage</p>
              <p className="text-2xl font-bold text-green-900">
                {analytics.totalUsage}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Avg. Usage</p>
              <p className="text-2xl font-bold text-purple-900">
                {analytics.totalDrafts > 0
                  ? Math.round(analytics.totalUsage / analytics.totalDrafts)
                  : 0}
              </p>
            </div>
            <BarChart3 className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Categories</p>
              <p className="text-2xl font-bold text-orange-900">
                {Object.keys(analytics.categoryStats).length}
              </p>
            </div>
            <FileText className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Popular and Recent Drafts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Popular Drafts */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-4">
            <Star className="h-5 w-5 text-yellow-500" />
            <h4 className="font-medium text-gray-900">
              Most Popular Templates
            </h4>
          </div>
          <div className="space-y-3">
            {analytics.popularDrafts.length > 0 ? (
              analytics.popularDrafts.map((draft, index) => (
                <div
                  key={draft.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">
                      {draft.name}
                    </p>
                    <p className="text-xs text-gray-600">{draft.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600">
                      {draft.usageCount || 0} uses
                    </p>
                    <p className="text-xs text-gray-500">#{index + 1}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No usage data yet</p>
            )}
          </div>
        </div>

        {/* Recently Created/Updated */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-4">
            <Clock className="h-5 w-5 text-blue-500" />
            <h4 className="font-medium text-gray-900">Recent Activity</h4>
          </div>
          <div className="space-y-3">
            {analytics.recentDrafts.length > 0 ? (
              analytics.recentDrafts.map((draft) => (
                <div
                  key={draft.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">
                      {draft.name}
                    </p>
                    <p className="text-xs text-gray-600">{draft.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {formatDate(draft.updatedAt)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {draft.usageCount || 0} uses
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No templates created yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(analytics.categoryStats).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-4">
            <BarChart3 className="h-5 w-5 text-purple-500" />
            <h4 className="font-medium text-gray-900">Usage by Category</h4>
          </div>
          <div className="space-y-3">
            {Object.entries(analytics.categoryStats)
              .sort(([, a], [, b]) => b.totalUsage - a.totalUsage)
              .map(([category, stats]) => (
                <div
                  key={category}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-medium text-gray-900">
                      {category}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({stats.count} templates)
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {stats.totalUsage} total uses
                      </p>
                      <p className="text-xs text-gray-500">
                        {stats.count > 0
                          ? Math.round(stats.totalUsage / stats.count)
                          : 0}{" "}
                        avg per template
                      </p>
                    </div>
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-medical-blue h-2 rounded-full"
                        style={{
                          width: `${
                            analytics.totalUsage > 0
                              ? (stats.totalUsage / analytics.totalUsage) * 100
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Insights</h4>
        <div className="text-sm text-blue-800 space-y-1">
          {analytics.totalDrafts === 0 && (
            <p>
              • Start by creating your first visit template to speed up data
              entry
            </p>
          )}
          {analytics.totalDrafts > 0 && analytics.totalUsage === 0 && (
            <p>
              • Your templates are ready! Start using them when creating new
              visits
            </p>
          )}
          {analytics.totalUsage > 0 && (
            <p>
              • Great job! Templates have been used {analytics.totalUsage}{" "}
              times, saving valuable time
            </p>
          )}
          {analytics.popularDrafts.length > 0 &&
            analytics.popularDrafts[0].usageCount > 5 && (
              <p>
                • Consider creating variations of your most popular template: "
                {analytics.popularDrafts[0].name}"
              </p>
            )}
          <p>
            • Templates help ensure consistent documentation and reduce data
            entry time
          </p>
        </div>
      </div>
    </div>
  );
};

export default DraftAnalytics;
